import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    createEmployee,
    getEmployeeTypes,
} from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { notify } from '../../../utils/notification';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_EMPLOYEES } from '../../../Routing/routes';
import EmployeeForm from './EmployeeForm';
import { useSocietyRoles, roleToOption } from '../../../hooks/useSocietyRoles';
import {
    emptyProfile,
    normalizeProfileInput,
    validateProfile,
    buildProfilePayload,
    splitProfile,
} from '../../../consts/employeeProfileFields';
import {
    emptyShift,
    validateShift,
    buildShiftPayload,
    splitShift,
} from '../../../consts/employeeShiftFields';

const initialForm = {
    employee_type: '',
    role_id: '',
    designation: '',
    department: '',
    employment_type: '',
    name: '',
    username: '',
    phone: '',
    email: '',
    address: '',
    // Joining date is when the person actually joined the society/role —
    // distinct from the row's created_at (when we entered them into the
    // portal). Defaults to today because that matches the common case
    // (adding someone who just started).
    joining_date: '',
    password: '',
    confirm_password: '',
    ...emptyProfile,
    ...emptyShift,
};

const AddEmployee = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {
        createEmployeeLoading,
        employeeTypesData,
        employeeTypesLoading,
    } = useSelector(state => state.EmployeeReducer || {});

    const [formData, setFormData] = useState(initialForm);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getEmployeeTypes({ }));
    }, [dispatch]);

    // Access roles of this society, for the Access Role picker.
    const { roles, loading: rolesLoading } = useSocietyRoles(selectedSocietyStorer.getSocietyId());
    const roleOptions = useMemo(() => roles.map(roleToOption), [roles]);

    const employeeTypeOptions = useMemo(() => (
        (Array.isArray(employeeTypesData) ? employeeTypesData : []).map(t => ({
            value: t.id ?? t.value ?? t.code,
            label: t.name ?? t.label ?? t.title ?? String(t.id),
        }))
    ), [employeeTypesData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: normalizeProfileInput(name, value) }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const e = {};
        if (!formData.employee_type) e.employee_type = 'Employee type is required';
        if (!formData.role_id) e.role_id = 'Access role is required';
        if (!formData.name.trim()) e.name = 'Full name is required';
        if (!formData.username.trim()) e.username = 'User name is required';
        else if (!/^[a-zA-Z0-9._-]{3,}$/.test(formData.username.trim())) e.username = 'Min 3 chars, letters / numbers / . _ -';
        if (!formData.phone.trim()) e.phone = 'Phone is required';
        else if (!/^\d{10}$/.test(formData.phone.trim())) e.phone = 'Phone must be exactly 10 digits';
        if (!formData.email.trim()) e.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email format';
        if (!formData.joining_date) e.joining_date = 'Joining date is required';
        if (!formData.address.trim()) e.address = 'Address is required';
        if (!formData.password) e.password = 'Password is required';
        else if (formData.password.length < 8) e.password = 'Min 8 characters';
        if (!formData.confirm_password) e.confirm_password = 'Confirm your password';
        else if (formData.confirm_password !== formData.password) e.confirm_password = 'Passwords do not match';
        Object.assign(e, validateProfile(formData));
        Object.assign(e, validateShift(formData));
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!validate()) return;

        // The BE validator requires a numeric society_id. If the storer can't
        // resolve one (SelectedUserSociety wiped, UserData missing), stop the
        // submit instead of shipping a null that server rejects with a
        // technical-looking error.
        const societyIdRaw = selectedSocietyStorer.getSocietyId();
        const society_id = societyIdRaw ? Number(societyIdRaw) : null;
        if (!society_id) {
            notify('error', 'No society context found. Please pick a society from the header and try again.');
            return;
        }

        const trimmed = formData.name.trim();
        const spaceIdx = trimmed.indexOf(' ');
        const first_name = spaceIdx === -1 ? trimmed : trimmed.slice(0, spaceIdx);
        const last_name  = spaceIdx === -1 ? '' : trimmed.slice(spaceIdx + 1).trim();

        const { shift, rest: withoutShift } = splitShift(formData);
        const { profile, rest: base } = splitProfile(withoutShift);
        const { confirm_password, employee_type, name, role_id, employment_type, ...rest } = base;
        dispatch(createEmployee({
            ...rest,
            ...buildProfilePayload(profile, { isEdit: false }),
            // Omitted when no shift was picked. Unlike the update endpoint this
            // one IS transactional: if the shift half fails the employee is
            // rolled back too, so a failure here really does mean nothing saved.
            ...buildShiftPayload(shift, undefined),
            first_name,
            last_name,
            employee_type_id: employee_type,
            // Left out when blank: the API rejects an empty employment_type.
            ...(employment_type ? { employment_type } : {}),
            // Explicit access role - no longer derived from the employee type.
            role_id: Number(role_id),
            society_id,
            // The backend links this employee to a User row. On fresh creation
            // the FE has no user_id yet — the backend auto-provisions from
            // email+password. If a caller wants to link an existing user (e.g.
            // via the search-by-email flow), pass that user_id here instead.
            user_id: null,
            onSuccess: () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES),
        }));
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                <button
                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES)}
                    className="tw-p-2 tw-rounded-md tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                >
                    <Icon icon="mdi:arrow-left" className="tw-text-gray-600" />
                </button>
                <div>
                    <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Add Employee</h1>
                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Create a new employee record</p>
                </div>
            </div>

            <EmployeeForm
                formData={formData}
                errors={errors}
                onChange={handleChange}
                onSubmit={handleSubmit}
                onCancel={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES)}
                submitting={createEmployeeLoading}
                mode="add"
                employeeTypeOptions={employeeTypeOptions}
                employeeTypesLoading={employeeTypesLoading}
                roleOptions={roleOptions}
                rolesLoading={rolesLoading}
            />
        </div>
    );
};

export default AddEmployee;
